import React, { useState, useEffect, useReducer, useMemo, useCallback, useRef } from "react";
import { getAuthToken } from "../api/GlobalApi";
import { LocalStorageTypes, IFormErrorMessage } from "../components/interfaces/Global";
import { APIHeader, isGuid, validateEmail, guid, handleInputType, convertCamelCase } from "../Helpers";
import { Environments, GameChangerSources, GlobalConstants, HolidayConstants, MsalConstants } from "../components/constants/GlobalConstants";
import { useBCGlobalReducer, Reducer } from "../components/BCGlobalStore";
import { allMockApiCalls } from "../api/mock/MockGlobalApi";
import { globalFilteredData } from "./GlobalStorageHelper";
import { GlobalStoreConstants } from "../components/constants/GlobalStoreConstants";
import BCValidationMessage from "../components/ui/BCValidationMessage";
import {
  ValidationsObjetType,
  ICalculatedFieldsOptions,
  BCFormController,
  AddressFieldTypes,
  PayloadContentType,
  ITriggerField,
  IsHiddenDisabledOperationTypes,
  IsHiddenOrDisabledFuncType,
  OnDropdownOptionsLoadedFuncType,
  ValidationOptionsType
} from "../components/lms/ui/LmsFormGenerator";
import { useLocation, useHistory } from "react-router-dom";
import { useBCGlobalCacheReducer } from "../components/BCGlobaleCacheStore";
import { FeatureFlags } from "../components/constants/LocalServerConstants";
import { getGeocode } from "use-places-autocomplete";
import { getIsWhiteLabeled } from "./PermissionHelper";
import { useLMSGlobalReducer } from "../components/lms/LmsGlobalStore";

const runAsyncFunction = (func, ...args) => {
  if (func) {
    (async () => {
      try {
        await func(...args);
      } catch (error) {
        if (FeatureFlags.IS_DEBUG) {
          console.error(error);
        }
      }
    })();
  }
};

export const getIsGreaterThanMobile = () => {
  return window.innerWidth > GlobalConstants.ScreenSizes.MOBILE_SIZE;
};

export const getIsGreaterThanTablet = () => {
  return window.innerWidth > GlobalConstants.ScreenSizes.TABLET_SIZE;
};

export type StorageTypes = "local" | "session";

export const localStorageGet = (key: string, storageType: StorageTypes = "local") => {
  let item: any = null;
  if (storageType === "local") {
    item = localStorage.getItem(key);
  }

  if (storageType === "session") {
    item = sessionStorage.getItem(key);
  }

  if (!item) return;
  if (item[0] === "{" || item[0] === "[" || item === "true" || item === "false") return JSON.parse(item);
  return item;
};

export const localStorageSave = (key: string, value: LocalStorageTypes, storageType: StorageTypes = "local") => {
  if (value === undefined) {
    if (FeatureFlags.IS_DEBUG) {
      console.log("Can't store undefined value");
    }

    return;
  }

  if (typeof value === "object" || Array.isArray(value)) {
    value = JSON.stringify(value);
  }

  if (typeof value !== "string") {
    if (FeatureFlags.IS_DEBUG) {
      console.log("Can't store unrecognized format value");
    }

    return;
  }

  if (storageType === "local") {
    localStorage.setItem(key, value);
  }

  if (storageType === "session") {
    sessionStorage.setItem(key, value);
  }
};

export const localStorageClear = (storageType: StorageTypes = "local", key = "") => {
  if (storageType === "local") {
    if (key) {
      localStorage.removeItem(key);
    } else {
      localStorage.clear();
    }
  }

  if (storageType === "session") {
    if (key) {
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.clear();
    }
  }
};

export const useLocalStorage = (key: string, initialValue: any, storageType: StorageTypes = "local") => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      return localStorageGet(key, storageType) || initialValue;
    } catch (error) {
      if (FeatureFlags.IS_DEBUG) {
        console.log(error);
      }
      return initialValue;
    }
  });
  const setValue = (value: LocalStorageTypes) => {
    try {
      setStoredValue(value);
      localStorageSave(key, value, storageType);
    } catch (error) {
      if (FeatureFlags.IS_DEBUG) {
        console.log(error);
      }
    }
  };

  useEffect(() => {
    if (key || initialValue) {
      return setStoredValue(localStorageGet(key, storageType) || initialValue);
    }
  }, [key, initialValue]);

  return [storedValue, setValue];
};

export const getParsedData = (data: any, dataType: PayloadContentType, excludeProperties: string[] = []) => {
  if (dataType === "JSON") {
    return typeof data !== "string" && data ? JSON.stringify(data) : data;
  }
  else {
    if (dataType === "FormData") {
      const formData = new FormData();

      if (data) {
        Object.entries(data).map(([key, value]) => {
          if (excludeProperties?.includes(key) === false) {
            if (Array.isArray(value)) {
              value.map((val, index) => {
                formData.append(`${key}[${index}]`, val);
              });
            }
            else {
              formData.append(key, value as any);
            }
          }
        });
      }

      return formData;
    }
  }

  return data;
};

export type MethodTypes = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface IFetchDataParams {
  proxyUserId?: string;
  mockCall?: any;
  queryId?: string;
  isFilterMetadata?: boolean;
  hiddenFilters?: any[];
  dispatch?: (any) => any;
  storageId?: string;
  useMock?: boolean;
  method?: MethodTypes;
  payloadData?: any;
  payloadType?: PayloadContentType;
  initialData?: any;
  defaultData?: any;
  formatDataFunc?: (data: any) => any;
  isDebug?: boolean;
  skipCache?: boolean;
  customHeaders?: Record<string, any>;
  isReady?: boolean;
  getResponseHeadersList?: string[];
  displayErrorGlobally?: boolean;
}

interface IFetchOptions {
  id?: string;
  fetchMethod?: MethodTypes;
  payloadDataType?: PayloadContentType
  data?: any;
  isHook?: boolean;
  useMockData?: boolean;
  skipCache?: boolean;
  skipSaveToCache?: boolean;
  onFinallyFunc?: () => any;
  initLoadingState?: boolean;
  defaultFetchData?: any;
  customFetchHeaders?: Record<string, any>;
  displayError?: boolean;
}

export interface IFetchError {
  message: string;
  errorResponse?: any;
}

type FetchErrorType = IFetchError | Error | null;

export const useFetchData = (
  suffix = "",
  {
    proxyUserId,
    mockCall,
    useMock = false,
    queryId = "",
    isFilterMetadata = false,
    hiddenFilters = [] as any,
    dispatch,
    storageId = "",
    method = "GET",
    payloadData,
    payloadType = "JSON",
    initialData = null,
    defaultData = null,
    formatDataFunc,
    isDebug = false,
    skipCache = false,
    customHeaders,
    isReady,
    getResponseHeadersList,
    displayErrorGlobally = false
  }: IFetchDataParams = {}
) => {
  const globalReducerInstance: any = useBCGlobalReducer();
  const globalDispatch = globalReducerInstance[1];
  const [{ apiCache }, globalCacheDispatch] = useBCGlobalCacheReducer();
  const initialCacheData = !skipCache ? apiCache[suffix] : undefined;
  const [data, setData] = useState(initialCacheData as any);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<FetchErrorType>(null);
  const [responseHeaders, setResponseHeaders] = useState(null as any);
  const [errorIsVisible, setErrorIsVisible] = useState(false);
  const url = process.env.REACT_APP_API_URL;
  const isMock = FeatureFlags.IS_MOCK || useMock;

  const saveApiData = (apiData: any, responseHeaders?: any): void => {
    if (formatDataFunc) {
      apiData = formatDataFunc(apiData);
    }

    setData(apiData);
    setLoading(false);
    if (responseHeaders) {
      setResponseHeaders(responseHeaders);
    }

    if (apiData && isFilterMetadata) {
      const dispatchPayload = {
        type: GlobalConstants.BCStoreReducerCases.SET_INITIAL_GLOBAL_FILTERS,
        payload: { filtersMetaData: apiData, hiddenFilters, storageId }
      };
      if (dispatch) {
        dispatch(dispatchPayload);
      }
      else {
        globalDispatch(dispatchPayload);
      }
    }
  };

  const saveError = (error: FetchErrorType): void => {
    setError(error);
    setErrorIsVisible(true);
    setLoading(false);

    if (dispatch) {
      const dispatchPayload = {
        type: GlobalConstants.BCStoreReducerCases.SET_INITIAL_GLOBAL_FILTERS,
        payload: { filtersMetaData: null, hiddenFilters, storageId }
      };

      if (dispatch) {
        dispatch(dispatchPayload);
      }
      else {
        if (isFilterMetadata) {
          globalDispatch(dispatchPayload);
        }
      }
    }
  };

  const setApiCash = (data: any, apiSuffix = suffix): void => {
    globalCacheDispatch({
      type: "SET_API_CACHE",
      payload: { data: data, apiSuffix },
    });
  };

  const resetLoadingState = (initLoadingState) => {
    if (initLoadingState) {
      setLoading(false);
    }
  };

  const fetchData = async (apiSuffix = suffix, options: IFetchOptions = {}) => {
    const {
      id = queryId as string,
      fetchMethod = method as MethodTypes,
      data = payloadData as any,
      payloadDataType = payloadType,
      isHook = false,
      useMockData = false,
      skipCache = false,
      skipSaveToCache = false,
      initLoadingState = false,
      defaultFetchData = defaultData,
      onFinallyFunc,
      customFetchHeaders = customHeaders,
      displayError = displayErrorGlobally
    } = options;

    const cacheData = apiCache && apiCache[apiSuffix] && fetchMethod === "GET" && !skipCache ? apiCache[apiSuffix] : null;

    if (cacheData || !apiSuffix) {
      return { data: cacheData };
    }
    else {
      if (isMock || useMockData) {
        const suffixId = apiSuffix?.length > GlobalConstants.DefaultValues.GUID_LENGTH ? apiSuffix.substring(apiSuffix.length - GlobalConstants.DefaultValues.GUID_LENGTH, apiSuffix.length) : null;
        const mockSuffix = suffixId && isGuid(suffixId) ? apiSuffix.replace(suffixId, "id") : apiSuffix;
        const mockApiCall = allMockApiCalls ? allMockApiCalls[mockSuffix] : mockCall;

        if (mockApiCall) {
          const mockResponse = await mockApiCall();

          if (mockResponse && mockResponse.ok) {
            const json = await mockResponse.json();
            if (isHook) {
              saveApiData(json);
            }

            setApiCash(json, apiSuffix);

            return { data: json };
          }
        }

        if (isHook) {
          saveError({ message: GlobalConstants.FetchApiErrors.MOCK });
        }

        return { error: { message: GlobalConstants.FetchApiErrors.MOCK } };
      }
      else {
        if (!isLoading && initLoadingState) {
          setLoading(true);
        }

        const parsedSuffix = apiSuffix && apiSuffix[0] && apiSuffix[0] === "/" ? apiSuffix.substring(1) : apiSuffix;
        let headers = APIHeader(proxyUserId, await getAuthToken(), payloadDataType);
        if (customFetchHeaders) {
          headers = { ...headers, ...customFetchHeaders };
        }

        const fetchOptions: any = {
          method: fetchMethod,
          headers,
        };

        if (fetchMethod !== "GET") {
          fetchOptions.body = getParsedData(data, payloadDataType);
        }

        const apiResponse: any = await fetch(`${url}${parsedSuffix}${id ? "/" + id : ""}`, fetchOptions)
          .catch(e => {
            if (isHook) {
              saveError({ message: e?.message });
            }
            else {
              if (FeatureFlags.IS_DEBUG) {
                console.log(e);
              }
            }
          })
          .finally(() => {
            if (onFinallyFunc) {
              onFinallyFunc();
            }
          });

        let formattedHeaders = null as any;
        if (apiResponse?.headers && getResponseHeadersList) {
          getResponseHeadersList.map((header) => {
            if (!formattedHeaders) {
              formattedHeaders = { [header]: apiResponse.headers.get(header) };
            }
            else {
              formattedHeaders[header] = apiResponse.headers.get(header);
            }
          });
        }

        if (apiResponse && apiResponse.status === 204) {
          if (isHook) {
            resetLoadingState(initLoadingState);

            if (defaultFetchData !== null) {
              saveApiData(defaultFetchData, formattedHeaders);
            }
          }
          else {
            return { data: defaultFetchData, status: apiResponse.status };
          }
        }
        else if (apiResponse && apiResponse.ok) {
          let json = null;

          try {
            json = await apiResponse.json();
          } catch (e) {
            if (FeatureFlags.IS_DEBUG && isDebug) {
              console.log(e);
            }
          }

          if (isHook) {
            saveApiData(json, formattedHeaders);
          }
          else {
            resetLoadingState(initLoadingState);
          }

          if (!skipSaveToCache) {
            setApiCash(json, apiSuffix);
          }

          if (FeatureFlags.IS_DEBUG && isDebug) {
            console.log(json);
          }

          return { data: json, status: apiResponse.status };
        }
        else {
          let errorResponse: any;
          try {
            errorResponse = await apiResponse.json();
          } catch (e) {
            if (FeatureFlags.IS_DEBUG && isDebug) {
              console.log(e);
            }
          }
          const message = errorResponse?.errorMessage || apiResponse?.statusText || GlobalConstants.FetchApiErrors.API;

          if (displayError) {
            globalDispatch({
              type: GlobalConstants.BCStoreReducerCases.SET_INFO,
              payload: { errorResponse, message, type: GlobalConstants.ErrorLevels.ERROR },
            });
          }

          if (isHook) {
            saveError({ message, errorResponse });
          }
          else {
            resetLoadingState(initLoadingState);
          }

          if (FeatureFlags.IS_DEBUG && isDebug) {
            console.log({ errorResponse, apiResponse });
          }

          return { error: { message, errorResponse, errorTitle: errorResponse?.errorTitle } };
        }
      }
    }
  };

  const callApi = async (skipCacheNow = skipCache): Promise<void> => {
    try {
      const cacheData = (apiCache && apiCache[suffix]) || initialData;
      if (!cacheData && !isLoading) {
        setLoading(true);
      }
      if (!skipCache && !skipCacheNow && cacheData) {
        saveApiData(cacheData);
      }
      else {
        await fetchData(suffix, { isHook: true, skipCache: skipCacheNow, initLoadingState: true });
      }
    } catch (e) {
      saveError(e as Error);
    }
  };

  const callApiNow: any = (loading = true) => {
    if (suffix || (useMock && mockCall)) {
      if (loading) {
        setLoading(true);
      }

      callApi(true).catch(e => {
        if (FeatureFlags.IS_DEBUG) {
          console.log(e);
        }
      });
    }
    else {
      if (FeatureFlags.IS_DEBUG) {
         console.log("Api suffix or mock data is missing");
      }
    }
  };

  useEffect(() => {
    if ((suffix || (useMock && mockCall)) && isReady !== false && !isLoading) {
      runAsyncFunction(callApi);
    }
  }, [suffix, queryId, isReady]);

  return [
    {
      data,
      isLoading,
      error,
      responseHeaders,
      errorIsVisible,
      setLoading,
      setError,
      setErrorIsVisible,
      setData,
      fetchData,
      globalReducerInstance,
      globalCacheDispatch
    }, callApiNow];
};

export const getEnvironmentDomain = () => {
  if (process.env.REACT_APP_ENV === "DEV") {
    return ".serviceyourloan-dev.com";
  }
  else if (process.env.REACT_APP_ENV === "UAT") {
    return ".serviceyourloan-uat.com";
  }
  else if (process.env.REACT_APP_ENV === "PRODUCTION") {
    return ".serviceyourloan.com";
  }

  return "";
};

export const getEnvironment = () => {
  if (process.env.REACT_APP_ENV === "DEV") {
    return Environments.DEVELOPMENT;
  }
  else if (process.env.REACT_APP_ENV === "UAT") {
    return Environments.STAGING;
  }
  else if (process.env.REACT_APP_ENV === "PREPROD") {
    return Environments.PREPROD;
  }
  else if (process.env.REACT_APP_ENV === "PRODUCTION") {
    return Environments.PRODUCTION;
  }

  return Environments.LOCAL;
};

export const getSubdomainFromUrl = () => {
  const subdomainSplits = window.location.host.split(MsalConstants.ServiceYourLoanSplit);
  if (subdomainSplits[0] && subdomainSplits[1] && subdomainSplits[0].toLocaleLowerCase()) {
    return subdomainSplits[0];
  }

  return null;
};

export const getSpaRedirectUrl = () => {
  if (getEnvironment() === Environments.LOCAL) {
    return "http://localhost:3000";
  }
  else if (getEnvironment() === Environments.DEVELOPMENT) {
    return "https://portal.serviceyourloan-dev.com";
  }
  else if (getEnvironment() === Environments.STAGING) {
    return "https://portal.serviceyourloan-uat.com";
  }
  else if (getEnvironment() === Environments.PREPROD) {
    return "https://portal.serviceyourloan-preprod.com";
  }
  else if (getEnvironment() === Environments.PRODUCTION) {
    return "https://portal.serviceyourloan.com";
  }

  return "";
};

interface IFormOptions {
  initIsDisabled?: boolean;
  isDisabled?: boolean;
  validations?: ValidationsObjetType;
  calculatedFields?: ICalculatedFieldsOptions;
  calculatedTargetFields?: ICalculatedFieldsOptions;
  calculatedTriggerFields?: ITriggerField[];
  numberFields?: string[];
  dataFormatterFunc?: any;
  recordId?: string;
  clearSuffixesOnSubmit?: string[];
  hiddenFieldsRules?: Record<string, any>;
  disabledFieldsRules?: Record<string, any>;
  allDropdownOptions?: any;
  onDropdownOptionsLoaded?: OnDropdownOptionsLoadedFuncType;
  onFormChange?: (any) => any;
  isDebug?: boolean
  myForm?: BCFormController
  initFileDownloads?: Record<string, any>
  exitEditOnClose?: boolean
  onSubmitSuccess?: (arg0?: any, arg1?: any) => any;
  onSubmitSuccessMessage?: string;
  successMessageFormatterFunc?: any;
  skipAutoCloseSuccessMessage?: boolean;
  onSaveContinue?: (arg0?: any, arg1?: any) => any;
  displayMessageLocally?: boolean;
  addressFields?: Record<string, any>;
  mailingIsSameAsPhysicalFieldName?: string;
  isLoading?: boolean;
}

type IsHiddenConditionType = Record<string, any>;
export interface IHandleSubmitOptions {
  method: MethodTypes
  payloadDataType?: PayloadContentType
  data: any
  buttonText: string
  onSuccessFunc?: (arg0?: any, arg1?: any) => any
  clearSuffixes?: string[]
  refreshDataOnSuccess?: boolean
  useFormInstance?: any
  actionName?: string,
  actionSuccessMessage?: string,
  alertMessage?: string
  showSuccessUiMessage?: boolean;
  spreadFormDataArrays?: boolean
  successMessageFormatterFunc?: any,
}

const getFormFieldsLength = (fields) => {
  let length = 0;
  if (fields && Array.isArray(fields)) {
    fields.map(field => {
      if (field.groupFields && Array.isArray(field.groupFields)) {
        length += field?.groupFields?.length;
      }
      else {
        length++;
      }
    });
  }

  return length;
};

export const getFieldHasValidation = (
  {
    name = "" as string,
    validationName = "" as ValidationOptionsType,
    formValidations = {} as ValidationsObjetType,
    useFormOptions = {} as any,
    inputs = {} as any
  }
) => {
  if (!formValidations[name]) {
    return false;
  }
  else if (Array.isArray(formValidations[name])) {
    if (formValidations[name]?.includes(validationName)) {
      return true;
    }
    else {
      let conditionalValidation = false as any;
      formValidations[name]?.map((validation) => {
        if (typeof validation === "object")
          if (typeof validation[validationName] === "function") {
            conditionalValidation = validation[validationName]({ ...useFormOptions, fieldName: name });
          }
          else if (typeof validation[validationName] === "object") {
            Object.entries(validation[validationName]).map(([key, value]: [string, any]) => {
              if (value && typeof value === "object" && "value" in value) {
                conditionalValidation = value.value === inputs[key];
                if (value.operator === "notequal") {
                  conditionalValidation = !conditionalValidation;
                }
              }
              else {
                if (inputs[key] === value) {
                  conditionalValidation = true;
                }
              }
            });
          }
      });

      return conditionalValidation;
    }
  }
  else {
    return false;
  }
};

export const useForm = (initial = {} as any, options = {} as IFormOptions) => {
  const [inputs, setInputs] = useState({ ...initial });
  const initialValues = JSON.stringify(initial, (key, value) => { return value === null ? "" : value; });
  const [isFormInitLoaded, setIsFormInitLoaded] = useState(false);
  const [formFields, setFormFields] = useState(undefined as any);
  const myFormFieldsLength = getFormFieldsLength(options?.myForm?.fields);
  const formFieldsLength = getFormFieldsLength(formFields);
  const displayValues = options?.myForm?.displayValues;
  const maskedValues = options?.myForm?.maskedValues;
  const [metaData, setMetaData] = useState(options?.myForm?.metaData);
  const isDebug = options?.myForm?.isDebug || options.isDebug;
  const [lastUpdated, setLastUpdated] = useState("");
  const [lastUpdatedNestedProp, setLastUpdatedNestedProp] = useState("");
  const [lastValue, setLastValue] = useState(undefined as any);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const initialErrors = options?.myForm?.errors || {} as Record<string, any>;
  const [errors, setErrors] = useState({});
  const [formErrorMessage, setFormErrorMessage] = useState(undefined as IFormErrorMessage | undefined);
  const [fileInput, setFileInput] = useState(null as any);
  const [fileInputs, setFileInputs] = useState(null as any);
  const initialFileDownloads = options?.myForm?.initFileDownloads || options.initFileDownloads || {};
  const initialFieldDownloadsString = JSON.stringify(initialFileDownloads);
  const [fileDownloads, setFileDownloads] = useState({} as Record<string, any>);
  const dataFormatterFunc = options?.myForm?.dataFormatterFunc || options.dataFormatterFunc;
  const initialValidations = options?.myForm?.validations || options.validations || {} as ValidationsObjetType;
  const [validations, setValidations] = useState({} as any);
  const calculatedFields = options?.myForm?.calculatedFields || options.calculatedFields || {} as ICalculatedFieldsOptions;
  const calculatedTargetFields = options?.myForm?.calculatedTargetFields || options.calculatedTargetFields || {} as Record<string, any>;
  const calculatedTriggerFields = options?.myForm?.calculatedTriggerFields || options.calculatedTriggerFields as ITriggerField[] | undefined;
  const hiddenFieldsRules = options?.myForm?.hiddenFieldsRules || options.hiddenFieldsRules || {} as Record<string, any>;
  const [hiddenFields, setHiddenFields] = useState(options?.myForm?.hiddenFields as Record<string, any>);
  const disabledFieldsRules = options?.myForm?.disabledFieldsRules || options.disabledFieldsRules || {} as Record<string, any>;
  const [disabledFields, setDisabledFields] = useState(options?.myForm?.disabledFields as Record<string, any>);
  const numberFields = options?.myForm?.numberFields || options.numberFields;
  const addressFields = options?.myForm?.addressFields || options.addressFields;
  const mailingIsSameAsPhysicalFieldName = options?.myForm?.mailingIsSameAsPhysicalFieldName || options.mailingIsSameAsPhysicalFieldName;
  const [hasAllAddressFields, setHasAllAddressFields] = useState(false);
  const excludeFromSubmit = options?.myForm?.excludeFromSubmit;
  const onSubmitSuccess = options?.myForm?.onSubmitSuccess || options.onSubmitSuccess;
  const onSubmitSuccessMessage = options?.myForm?.onSubmitSuccessMessage || options.onSubmitSuccessMessage;
  const skipAutoCloseSuccessMessage = options?.myForm?.skipAutoCloseSuccessMessage || options.skipAutoCloseSuccessMessage;
  const onSaveContinue = options?.myForm?.onSaveContinue || options.onSaveContinue;
  const exitEditOnClose = options?.myForm?.exitEditOnClose || options.exitEditOnClose;
  const recordId = initial.id || options?.myForm?.recordId || options.recordId || "";
  const clearSuffixesOnSubmit = options?.myForm?.clearSuffixesOnSubmit ? options?.myForm?.clearSuffixesOnSubmit : options.clearSuffixesOnSubmit ? options.clearSuffixesOnSubmit : options?.myForm?.deleteSuffix ? [options?.myForm?.deleteSuffix] : [] as string[];
  const defaultSubmitMethod = recordId ? GlobalConstants.FetchMethods.PUT as MethodTypes : GlobalConstants.FetchMethods.POST as MethodTypes;
  const defaultSuccessMessageFormatterFunc = options?.myForm?.successMessageFormatterFunc || options.successMessageFormatterFunc;
  const uploadDocSuffix = options?.myForm?.uploadDocSuffix;
  const [{ data: responseData, isLoading: submitting, error: submitError, setLoading: setSubmitting, setError: setSubmitError, setData, fetchData }] = useFetchData();
  const globalCacheDispatch = useBCGlobalCacheReducer()[1];
  const globalDispatch = useBCGlobalReducer()[1];
  const lmsGlobalDispatch = useLMSGlobalReducer()[1];
  const [activeButtonText, setActiveButtonText] = useState("");
  const [success, setSuccess] = useState(false);
  const [displayMessageLocally, setDisplayMessageLocally] = useState(options?.myForm?.displayMessageLocally || !!options?.displayMessageLocally);
  const [confirmOptions, setConfirmOptions] = useState({} as any);
  const [modalIframeSrcPath, setModalIframeSrcPath] = useState("");
  const [modalIframeSize, setModalIframeSize] = useState("");
  const [modalIframeHeight, setModalIframeHeight] = useState("");
  const iframeModalRef = useRef(null as any);
  const history = useHistory();
  const [allDropdownOptionsData, setAllDropdownOptionsData] = useState(undefined as Record<string, any> | undefined);
  const { promisesData: promiseResDropdownOptions, promisesLoading: allDropdownOptionsIsLoading } = usePromiseAll(options?.myForm?.allDropdownOptions, {
    isReady: options?.myForm?.allDropdownOptions?.length > 0 && !allDropdownOptionsData && options?.myForm?.allDropdownOptionsAreReady !== false,
    defaultData: []
  });

  const useFormOptions = {
    inputs,
    setInputs,
    errors,
    setErrors,
    formFields,
    setFormFields,
    myFormFields: options?.myForm?.fields,
    calculatedFields,
    hiddenFieldsRules,
    disabledFieldsRules,
    allDropdownOptionsData,
    setAllDropdownOptionsData,
    hiddenFields,
    setHiddenFields,
    metaData,
    setMetaData
  };

  const handleConfirmFunc = (funcToConfirm, confirmMessage, isAlert = false, confirmButtonText = "OK") => {
    if (funcToConfirm && confirmMessage) {
      setConfirmOptions(createConfirmationObj(funcToConfirm, confirmMessage, isAlert, confirmButtonText));
    }
    else {
      safelyRunFunction(funcToConfirm);
    }
  };

  const setAlertMessage = (alertMessage, funcToRun = undefined as any) => {
    if (alertMessage) {
      setConfirmOptions(createConfirmationObj(funcToRun, alertMessage, true));
    }
  };

  useEffect(() => {
    if (options.allDropdownOptions) {
      setAllDropdownOptionsData((prevDropdownOptions) => ({ ...prevDropdownOptions, ...options.allDropdownOptions  }));
    }
  }, [options.allDropdownOptions]);

  useEffect(() => {
    if (!allDropdownOptionsIsLoading && promiseResDropdownOptions) {
      const dropdownOptionsDataArrays = options?.myForm?.dropdownOptionsDataArrays || {};
      const allOptions = { ...dropdownOptionsDataArrays, ...promiseResDropdownOptions };
      setAllDropdownOptionsData(allOptions);

      if (options?.myForm?.onDropdownOptionsLoaded) {
        options?.myForm?.onDropdownOptionsLoaded(allOptions, useFormOptions);
      }
    }
  }, [promiseResDropdownOptions, allDropdownOptionsIsLoading, initialValues, options.isLoading]);

  const updateAllDropdownOptions = (suffix, dropdownOptions, skipSaveToCache = false) => {
    setAllDropdownOptionsData({ ...allDropdownOptionsData, [suffix]: dropdownOptions });
    if (!skipSaveToCache) {
      globalCacheDispatch({
        type: GlobalConstants.BCStoreReducerCases.SET_API_CACHE,
        payload: { apiSuffix: suffix, data: dropdownOptions },
      });
    }
  };

  const refreshDropdownOptions = async (suffix, skipSaveToCache = false, onCompleteFunc = undefined as any) => {
    if (suffix && options?.myForm?.allDropdownOptions?.includes(suffix)) {
      const { data } = await fetchData(suffix, { skipCache: true });
      if (data) {
        updateAllDropdownOptions(suffix, data, skipSaveToCache);
      }
      if (onCompleteFunc && typeof onCompleteFunc === "function") {
        onCompleteFunc(data);
      }
    }
  };

  const formatInputs = useCallback(() => {
    let formattedRes = { ...inputs };

    if (excludeFromSubmit && excludeFromSubmit.length > 0) {
      formattedRes = {};
      Object.entries(inputs).map(([key, value]) => {
        if (!excludeFromSubmit.includes(key)) {
          formattedRes[key] = value;
        }
      });
    }

    if (numberFields) {
      numberFields.map(nField => {
        if (formattedRes && formattedRes[nField] && typeof formattedRes[nField] === "string") {
          formattedRes[nField] = parseFloat(formattedRes[nField]);
        }
      });
    }

    if (dataFormatterFunc && typeof dataFormatterFunc === "function") {
      return dataFormatterFunc(formattedRes);
    }

    if (maskedValues) {
      Object.entries(maskedValues).map(([key, value]) => {
        if (!inputs[key]) {
          formattedRes[key] = value;
        }
      });
    }

    return formattedRes;
  }, [inputs]);

  const formattedInputs = useMemo(() => formatInputs(), [inputs]);

  const handleHasAllAddress = (fields = addressFields, newInputs = inputs) => {
    if (fields) {
      const isNotFull = Object.keys(fields).some(af => !newInputs[af]);
      if (isNotFull) {
        if (hasAllAddressFields) {
          setHasAllAddressFields(false);
        }
      }
      else {
        if (!hasAllAddressFields) {
          setHasAllAddressFields(true);
        }
      }
    }
  };

  useEffect(() => {
    if (options?.myForm?.fields) {
      setFormFields(options?.myForm?.fields);
    }
  }, [myFormFieldsLength, initialValues]);

  useEffect(() => {
    if (options?.myForm?.metaData) {
      setMetaData(options?.myForm?.metaData);
    }
  }, [JSON.stringify(options?.myForm?.metaData)]);

  useEffect(() => {
    if (Object.keys(initialValidations)?.length !== Object.keys(validations)?.length) {
      setValidations(initialValidations);
    }
  }, [Object.keys(initialValidations)?.length]);

  useEffect(() => {
    if (Object.keys(initialErrors)?.length !== Object.keys(errors)?.length) {
      setErrors(initialErrors);
    }
  }, [JSON.stringify(initialErrors)]);

  useEffect(() => {
    if (initialFieldDownloadsString !== JSON.stringify(fileDownloads)) {
      setFileDownloads(initialFileDownloads);
    }
  }, [initialFieldDownloadsString]);

  useEffect(() => {
    let calculatedInitial = initial;
    Object.keys(initial).map(key => {
      if (calculatedFields[key]) {
        calculatedInitial = calculateFields(calculatedInitial, calculatedFields[key], useFormOptions);
      }
    });

    if (calculatedTargetFields) {
      Object.values(calculatedTargetFields).map(calculatedOptions => {
        calculatedInitial = calculateFields(calculatedInitial, calculatedOptions, useFormOptions);
      });
    }

    if (addressFields && mailingIsSameAsPhysicalFieldName) {
      handleHasAllAddress();

      const isNotEqual = Object.entries(addressFields).some(([addressFieldName, mailingFieldName]) => {
        return !initial[addressFieldName] || initial[addressFieldName] !== initial[mailingFieldName];
      });

      if (!isNotEqual) {
        calculatedInitial[mailingIsSameAsPhysicalFieldName] = true;
      }
    }

    setInputs(calculatedInitial);
  }, [initialValues, myFormFieldsLength, formFieldsLength]);

  useEffect(() => {
    setIsDisabled(options?.myForm?.initIsDisabled || !!options?.initIsDisabled);
  }, [options?.myForm?.initIsDisabled, options?.initIsDisabled, options?.isDisabled]);

  useEffect(() => {
     let timoutFunc = null as any;
     if (!submitting) {
       timoutFunc = setTimeout(() => {
         setSubmitError(false);
         setSuccess(false);
       }, GlobalConstants.DefaultValues.CLEAR_INFO_TIME);
     }

     return () => {
       if (timoutFunc) {
         clearTimeout(timoutFunc);
       }
     };
  }, [submitting, submitError]);

  interface IHasErrorOptions {
    inputsProp?: string
    skipRequired?: boolean
    formInputs?: any
    formValidations?: ValidationsObjetType
  }

  const hasError = (name, options = {} as IHasErrorOptions) => {
    const { inputsProp = undefined, skipRequired = false, formInputs = inputs, formValidations = validations } = options;
    if (formValidations && formValidations[name]) {
      const isNested = inputsProp && formInputs[inputsProp];
      const inputs = isNested ? formInputs[inputsProp] : formInputs;
      const fieldValue = inputs?.[name];
      const allErrors = [] as any[];
      const allDetailedErrors = [] as string[];
      if (getFieldHasValidation({ name, validationName: "custom", formValidations, inputs })) {
        const { message, detailedMessage, fullMessage } = getFieldHasValidation({ name, validationName: "custom", formValidations });
        allErrors.push({
          message,
          fullMessage,
          detailedMessage,
        });
        allDetailedErrors.push(detailedMessage);
      }
      else if (!skipRequired && getFieldHasValidation({ name, validationName: "required", formValidations, inputs }) && isEmptyItem(isNested ? formInputs[inputsProp] : formInputs, name)) {
        const detailedMessage = "Please fill this required field.";
        allErrors.push({
          message: GlobalConstants.ValidationMessages.REQUIRED,
          fullMessage: "Field is required.",
          detailedMessage,
        });
        allDetailedErrors.push(detailedMessage);
      }
      else if (getFieldHasValidation({ name, validationName: "zipcode", formValidations, inputs }) && fieldValue && !isZipCode(fieldValue)) {
        const detailedMessage = "Please enter valid Zip Code with 6 or 10 characters.";
        allErrors.push({
          message: GlobalConstants.ValidationMessages.INVALID,
          fullMessage: "Zip Code field is invalid.",
          detailedMessage,
        });
        allDetailedErrors.push(detailedMessage);
      }
      else if (getFieldHasValidation({ name, validationName: "email", formValidations, inputs }) && fieldValue && !validateEmail(fieldValue)) {
        const detailedMessage = "Please enter valid email address.";
        allErrors.push({
          message: GlobalConstants.ValidationMessages.INVALID,
          fullMessage: "Email field is invalid.",
          detailedMessage,
        });
        allDetailedErrors.push(detailedMessage);
      }
      else if (getFieldHasValidation({ name, validationName: "website", formValidations, inputs }) && fieldValue && !isValidUrl(fieldValue)) {
        const detailedMessage = "Please enter valid website url.";
        allErrors.push({
          message: GlobalConstants.ValidationMessages.INVALID,
          fullMessage: "Website field is invalid.",
          detailedMessage,
        });
        allDetailedErrors.push(detailedMessage);
      }
      else if (getFieldHasValidation({ name, validationName: "phone", formValidations, inputs }) && fieldValue && !isValidPhoneNumber(fieldValue)) {
        const detailedMessage = "Please enter valid phone with minimum 10 characters.";
        allErrors.push({
          message: GlobalConstants.ValidationMessages.INVALID,
          fullMessage: "Phone field is invalid.",
          detailedMessage,
        });
        allDetailedErrors.push(detailedMessage);
      }
      else if (getFieldHasValidation({ name, validationName: "EIN/TIN", formValidations, inputs }) && fieldValue && !isValidEinTin(fieldValue)) {
        const detailedMessage = "Please enter at least 9 characters.";
        allErrors.push({
          message: GlobalConstants.ValidationMessages.INVALID,
          fullMessage: "EIN/TIN field is invalid.",
          detailedMessage,
        });
        allDetailedErrors.push(detailedMessage);
      }
      else if (getFieldHasValidation({ name, validationName: "abbreviation", formValidations, inputs }) && fieldValue && !isValidAbbreviation(fieldValue)) {
        const detailedMessage = "Abbreviation length must be between 0 and 10 characters.";
        allErrors.push({
          message: GlobalConstants.ValidationMessages.INVALID,
          fullMessage: "Abbreviation field is invalid.",
          detailedMessage,
        });
        allDetailedErrors.push(detailedMessage);
      }
      else if (getFieldHasValidation({ name, validationName: "amendment", formValidations, inputs }) && fieldValue && !isValidAmendment(fieldValue)) {
        const detailedMessage = "Extension length must be between 0 and 5 characters.";
        allErrors.push({
          message: "length must be between 0 and 5 characters",
          fullMessage: "Amendment field is invalid.",
          detailedMessage,
        });
        allDetailedErrors.push(detailedMessage);
      }
      if (getFieldHasValidation({ name, validationName: "routingNumber", formValidations, inputs }) && fieldValue && !isValidRoutingNumber(fieldValue?.toString())) {
        const detailedMessage = "Number length must be between 0 and 9.";
        allErrors.push({
          message: "length must be between 0 and 9",
          fullMessage: "Routing number field is invalid.",
          detailedMessage,
        });
        allDetailedErrors.push(detailedMessage);
      }
      else if (getFieldHasValidation({ name, validationName: "socialSecurityNumber", formValidations, inputs }) && fieldValue && !isValidSocialSecurityNumber(fieldValue?.toString())) {
        const detailedMessage = "Social Security Number length must be between 0 and 9.";
        allErrors.push({
          message: "length must be between 0 and 9",
          fullMessage: "Social Security Number field is invalid.",
          detailedMessage,
        });
        allDetailedErrors.push(detailedMessage);
      }
      else if (getFieldHasValidation({ name, validationName: "confirmPassword", formValidations, inputs }) && fieldValue && inputs?.password !== inputs?.confirmPassword) {
        const detailedMessage = "Passwords do not match";
        allErrors.push({
          message: "and Password do not match",
          fullMessage: "Passwords do not match",
          detailedMessage,
        });
        allDetailedErrors.push(detailedMessage);
      }
      else if (getFieldHasValidation({ name, validationName: "positiveNumber", formValidations, inputs }) && fieldValue !== undefined && !isValidPositiveNumber(fieldValue)) {
        const detailedMessage = "Filed must contain a positive value";
        allErrors.push({
          message: "must contain a positive value",
          fullMessage: "Filed must contain a positive value",
          detailedMessage,
        });
        allDetailedErrors.push(detailedMessage);
      }

      if (allErrors.length > 0) {
        return { ...allErrors[0], allErrors, allDetailedErrors };
      }
    }

    return null;
  };

  const validateForm = (inputsProp = undefined, skipRequired = false) => {
    const errors = {} as Record<string, any>;
    Object.keys(validations).map((name) => {
      const formFieldError = hasError(name, { inputsProp, skipRequired });
      const isFieldHidden = handleIsHidden({
        isHidden: hiddenFieldsRules[name]?.rule,
        isHiddenOperation: hiddenFieldsRules[name]?.operation,
        nestedInputsProps: inputsProp,
        name,
        isHiddenFunc: hiddenFieldsRules[name]?.func,
      });

      const isFieldDisabled = handleIsDisabled({
        isDisabledOn: disabledFieldsRules[name]?.rule,
        isDisabledOnOperation: disabledFieldsRules[name]?.operation,
        nestedInputsProps: inputsProp,
        name,
        isDisabledFunc: disabledFieldsRules[name]?.func,
      });

      if (formFieldError && !isFieldHidden && !isFieldDisabled) {
        if (inputsProp) {
          if (!errors[inputsProp]) {
            errors[inputsProp] = { [name]: formFieldError };
          }
          else {
            errors[inputsProp][name] = formFieldError;
          }
        }
        else {
          errors[name] = formFieldError;
        }
      }
    });

    if (Object.keys(errors).length === 0) {
      return null;
    }
    else {
      return errors;
    }
  };

  const copyPhysicalToMailingAddress = (newInputs) => {
    if (addressFields) {
      const newInputsCopy = { ...newInputs };
      Object.entries(addressFields).map(([addressFieldName, mailingFieldMapping]) => {
        newInputsCopy[mailingFieldMapping] = newInputsCopy[addressFieldName];
      });

      return newInputsCopy;
    }

    return newInputs;
  };

  const handleChange = (e, data, type = null as any, prop = null as any, onFieldChanged = null as any, charLimit = undefined as number | undefined) => {
    if (!type && e?.target?.type) {
      type = e?.target?.type;
    }

    let { value } = data;
    const { name } = data;
    if (type === "number") {
      value = parseFloat(value);
    }
    else if (type === "file" && e?.target?.files[0]) {
      setFileInput(e?.target?.files[0]);

      if (fileInputs) {
        setFileInputs({ ...fileInputs, [name]: e?.target?.files[0] });
      }
      else {
        setFileInputs({ [name]: e?.target?.files[0] });
      }
    } else {
      if (value && charLimit && value.length > charLimit) {
        value = value.substring(0, charLimit);
      }
    }

    if (name) {
      let newInputs;

      if (prop !== null && prop !== undefined) {
        const propInputs = inputs[prop] !== undefined ? { ...inputs[prop] } : {};
        newInputs = { ...inputs, [prop]: { ...propInputs, [name]: value } };
      }
      else {
        newInputs = {
          ...inputs,
          [name]: value
        };
      }

      if (calculatedFields && calculatedFields[name]) {
        newInputs = calculateFields(newInputs, calculatedFields[name]);
      }
      else {
        if (calculatedTriggerFields && Array.isArray(calculatedTriggerFields)) {
          calculatedTriggerFields.map(field => {
            if (field.name === name) {
              if (prop) {
                if (field.nestedInputsProp === prop) {
                  newInputs = calculateFields(newInputs, calculatedTargetFields[field.targetField], useFormOptions);
                }
              }
              else {
                newInputs = calculateFields(newInputs, calculatedTargetFields[field.targetField], useFormOptions);
              }
            }
          });
        }
      }

      if (onFieldChanged && typeof onFieldChanged === "function") {
        onFieldChanged({ name, value, type, oldInputs: inputs, newInputs, setInputs, errors, setErrors, fetchData });
      }

      if (addressFields && addressFields[name]) {
        handleHasAllAddress(addressFields, newInputs);
      }

      if (mailingIsSameAsPhysicalFieldName === name) {
        newInputs = copyPhysicalToMailingAddress(newInputs);
      }

      setInputs(newInputs);

      if (value !== null) {
        if (!lastUpdated) {
          lmsGlobalDispatch({ type: GlobalConstants.LMSStoreReducerCases.SET_TAB_HAS_UPDATED_FORM });
        }

        setLastUpdated(name);
        setLastUpdatedNestedProp(prop);
      }

      const hasNestedError = errors && errors[prop] && errors[prop][name];

      if ((errors && errors[name]) || hasNestedError) {
        const formFieldError = hasError(name, { inputsProp: prop, skipRequired: !!errors[prop], formInputs: newInputs });

        if (formFieldError) {
          if (errors[prop] && errors[prop][name]) {
            setErrors({ ...errors, [prop]: { ...errors[prop], [name]: formFieldError } });
          }
          else {
            setErrors({ ...errors, [name]: formFieldError });
          }
        }
        else {
          if (errors[prop] && errors[prop][name]) {
            setErrors({ ...errors, [prop]: { ...errors[prop], [name]: null } });
          }
          else {
            setErrors({ ...errors, [name]: null });
          }
        }
      }
    }
  };

  const updateInput = (name, value, prop = null as any) => {
    let newInputs: any;
    if (prop !== null && prop !== undefined) {
      const propInputs = inputs[prop] !== undefined ? { ...inputs[prop] } : {};
      newInputs = { ...inputs, [prop]: { ...propInputs, [name]: value } };
      setInputs(newInputs);
    }
    else {
      newInputs = {
        ...inputs,
        [name]: value
      };

      setInputs(newInputs);
    }

    return newInputs;
  };

  const removeInput = (name, prop = null as any) => {
    if (prop !== null && prop !== undefined) {
      const propInputs = inputs[prop] !== undefined ? { ...inputs[prop] } : {};
      const newInputs = { ...inputs, [prop]: { ...propInputs, [name]: null } };
      setInputs(newInputs);
    }
    else {
      const newInputs = {
        ...inputs,
        [name]: null
      };

      setInputs(newInputs);
    }
  };

  const updateDropdownFieldValue = async (dropdownOptionsSuffix, fieldName, newValue, prop = undefined as any) => {
    updateInput(fieldName, newValue, prop);
    const { data } = await fetchData(dropdownOptionsSuffix, { skipCache: true });
    if (data) {
      updateAllDropdownOptions(dropdownOptionsSuffix, data, true);
    }
  };

  const initSubmittingStates = (buttonText, skipDisabled = false) => {
    setSubmitting(true);
    if (!skipDisabled) {
      setIsDisabled(true);
    }
    setActiveButtonText(buttonText);
  };

  const resetSubmittingStates = (skipDisabled = false) => {
    setSubmitting(false);
    if (!skipDisabled) {
      setIsDisabled(false);
    }
    setActiveButtonText("");
  };

  const handleSubmit = (submitSuffix, {
    method = defaultSubmitMethod,
    data = formattedInputs,
    payloadDataType = "JSON",
    buttonText = "",
    onSuccessFunc = null as any,
    useFormInstance,
    actionName,
    actionSuccessMessage,
    showSuccessUiMessage = true,
    successMessageFormatterFunc = defaultSuccessMessageFormatterFunc,
  }: IHandleSubmitOptions) => {
    const previousTableSuffix = localStorageGet("prevtable", "session");
    const apiCacheToClear = previousTableSuffix ? [previousTableSuffix, ...clearSuffixesOnSubmit] : clearSuffixesOnSubmit;
    if (data && "mailingIsSameAsPhysical" in data) {
      if (data?.mailingIsSameAsPhysical) {
        copyPhysicalToMailingAddress(data);
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { mailingIsSameAsPhysical, ...dataCopy } = data;
      data = dataCopy;
    }

    if (FeatureFlags.IS_DEBUG && options.isDebug) {
      console.log(JSON.stringify(data, null, 2));
    }

    if (!submitting || activeButtonText !== buttonText) {
      initSubmittingStates(buttonText);
    }

    if (payloadDataType === "FormData" && fileInputs) {
      Object.entries(fileInputs).map(([name, fileObj]) => {
        if (data && data[name]) {
          data[name] = fileObj;
        }
      });
    }

    if (method === "POST" && data && !data.id && typeof data === "object") {
      if (Array.isArray(data) && data.length === 1 && typeof data[0] === "object") {
        data = [{ ...data[0], id: guid() }];
      }
      else {
        data.id = guid();
      }
    }

    fetchData(submitSuffix, { fetchMethod: method, data, payloadDataType }).then(response => {
      if (FeatureFlags.IS_DEBUG && isDebug) console.log({ response, submitSuffix, data, method, payloadDataType });

      if (!response.error) {
        setSuccess(true);
        setData(response.data);
        setLastUpdated("");
        setLastValue(undefined);

        if (fileInput && uploadDocSuffix) {
          const id = isGuid(response?.data) ? response?.data : response?.data?.id || data.id;
          if (id) {
            const orgFileData = { assetFile: fileInput, id };
            uploadFile(uploadDocSuffix, orgFileData).then(() => null).catch(e => {
              if (FeatureFlags.IS_DEBUG) {
                console.log(e);
              }
            });
          }
        }

        if (fileInputs && Object.keys(fileDownloads).length > 0) {
          const fileDownloadsCopy = { ...fileDownloads };
          Object.entries(fileInputs).map(([name, fileObj]: [string, any]) => {
            if (fileDownloadsCopy[name] && fileObj) {
              fileDownloadsCopy[name] = { ...fileDownloadsCopy[name], fileName: fileObj.name };
            }
          });

          setFileDownloads(fileDownloadsCopy);
        }

        if (showSuccessUiMessage) {
          const formattedSuccessMessage = successMessageFormatterFunc ? successMessageFormatterFunc(response?.data, useFormInstance) : null;
          globalDispatch({
            type: GlobalConstants.BCStoreReducerCases.SET_INFO,
            payload: {
              header: GlobalConstants.DefaultValues.messageHeaderSuccessText,
              message: formattedSuccessMessage || actionSuccessMessage || onSubmitSuccessMessage || GlobalConstants.DefaultValues.messageFormSubmitSuccess,
              type: GlobalConstants.ErrorLevels.INFO,
              skipAutoClose: skipAutoCloseSuccessMessage
            },
          });
        }

        if (apiCacheToClear && apiCacheToClear.length > 0) {
          globalCacheDispatch({
            type: GlobalConstants.BCStoreReducerCases.CLEAR_API_CACHE,
            payload: { apiSuffixes: apiCacheToClear },
          });

          localStorageClear("session", "prevtable");
        }

        if (onSubmitSuccess) {
          onSubmitSuccess(response?.data || data, useFormInstance);
        }

        if (onSuccessFunc && typeof onSuccessFunc === "function") {
          onSuccessFunc(response?.data || data, useFormInstance);
        }

        if (actionName === "save-continue") {
          if (onSaveContinue) {
            onSaveContinue(response?.data || data, useFormInstance);
          }
          else {
            const id = isGuid(response?.data?.id) ? response?.data?.id : isGuid(data?.id) ? data?.id : isGuid(data[0]?.id) ? data[0]?.id : "";
            const splitPath = window.location.pathname.split("/");
            const edithPath = splitPath.length > 1 ? `${splitPath.slice(0, splitPath.length - 1).join("/")}/${splitPath[splitPath.length - 1].replace("add", "edit")}` : "";
            const saveAndContinueSuffix = options?.myForm?.saveAndContinueSuffix || edithPath;

            if (id && saveAndContinueSuffix) {
              history.replace(appendIdToPath({ id, path: saveAndContinueSuffix }));
            }
          }
        }
      }
      else {
        if (!displayMessageLocally) {
          globalDispatch({
            type: GlobalConstants.BCStoreReducerCases.SET_INFO,
            payload: { ...response.error, type: GlobalConstants.ErrorLevels.ERROR },
          });
        }


        setSubmitError(response.error);
      }
    }).catch(e => {
      if (!displayMessageLocally) {
        globalDispatch({
          type: GlobalConstants.BCStoreReducerCases.SET_INFO,
          payload: { message: e?.message, type: GlobalConstants.ErrorLevels.ERROR },
        });
      }

      setSubmitError(e);
    }).finally(() => {
      setSubmitting(false);

      if (!exitEditOnClose || !actionName?.includes("close")) {
        setIsDisabled(false);
      }
    });
  };

  const handleDelete = (submitSuffix, { method = "DELETE" as MethodTypes, data = null as any, buttonText = "", onSuccessFunc }) => {
    if (submitSuffix && submitSuffix.includes(":id") && recordId) {
      submitSuffix = submitSuffix.replace(":id", recordId);
    } else {
      data = [recordId] as string[];
    }

    handleSubmit(submitSuffix, { method, buttonText, data, onSuccessFunc });
  };

  const handleDownloadFile = async (fileDownloadSuffix, downloadOptions: IDownloadOptions, buttonText) => {
    initSubmittingStates(buttonText || "");
    const error = await downloadFile(fileDownloadSuffix, downloadOptions) as any;
    if (error) {
      setFormErrorMessage(error);
    }

    resetSubmittingStates();
    return error;
  };

  const resetForm = () => {
    setInputs(initial);
    setMetaData(options?.myForm?.metaData);
    setFormFields(options?.myForm?.fields);
  };

  const clearForm = () => {
    const blankState = Object.fromEntries(
      Object.entries(inputs).map(([key]) => [key, ""])
    );
    setInputs(blankState);
  };

  const toggleDisabled = () => {
    setIsDisabled(!isDisabled);
  };

  const handleClose = (onClosePath = "") => {
    if (onClosePath) {
      if (onClosePath[0] !== "/") {
        onClosePath = `/${onClosePath}`;
      }

      history.push(onClosePath);
    }
    else {
      history.goBack();
    }
  };

  const handleIsDisabledOrHidden = (options) => {
    const {
      condition = undefined as Record<string, any> | undefined,
      operation = "" as IsHiddenDisabledOperationTypes,
      nestedInputsProp = undefined as any,
      name = "",
      hiddenOrDisabledFields = undefined as Record<any, any> | undefined,
      isHiddenOrDisabledFunc = undefined as any,
    } = options;
    let isHiddenOrDisabled = false;

    if (isHiddenOrDisabledFunc && typeof isHiddenOrDisabledFunc === "function") {
      return isHiddenOrDisabledFunc({ inputs, hiddenFields, disabledFields, metaData, isDisabled });
    }

    if (hiddenOrDisabledFields && nestedInputsProp !== undefined && hiddenOrDisabledFields[nestedInputsProp] && hiddenOrDisabledFields[nestedInputsProp][name]) {
      return true;
    }

    if (hiddenOrDisabledFields && nestedInputsProp === undefined && hiddenOrDisabledFields && hiddenOrDisabledFields[name]) {
      return true;
    }

    if (!condition && !operation) {
      return false;
    }
    else if (operation === "isEditOnly" && isDisabled) {
      return true;
    }
    else if (condition) {
      Object.entries(condition).map(([key, ruleValue]) => {
        const inputsValue = nestedInputsProp !== undefined && inputs && inputs[nestedInputsProp] ? inputs[nestedInputsProp][key] : inputs[key];
        if (operation === "isUndefined") {
          isHiddenOrDisabled = inputsValue !== "undefined";
        }
        else if (operation === "isFalsy") {
          isHiddenOrDisabled = !inputsValue;
        }
        else {
          if (typeof inputsValue !== "undefined" && !isHiddenOrDisabled) {
            if (operation === "includes") {
              if (ruleValue && Array.isArray(ruleValue)) {
                isHiddenOrDisabled = ruleValue.includes(inputsValue);
              }
            }
            else if (operation === "doesNotInclude") {
              if (ruleValue && Array.isArray(ruleValue)) {
                isHiddenOrDisabled = !ruleValue.includes(inputsValue);
              }
            }
            else if (operation === "equal") {
              isHiddenOrDisabled = inputsValue === ruleValue;
            }
            else {
              isHiddenOrDisabled = inputsValue !== ruleValue;
            }
          }
          else {
            if (operation === "notequal") {
              isHiddenOrDisabled = inputsValue !== ruleValue;
            }
          }
        }
      });
    }

    return isHiddenOrDisabled;
  };

  const handleIsHidden = (options) => {
    const {
      isHidden: isHiddenOn = undefined as IsHiddenConditionType | undefined,
      isHiddenOperation = "equal",
      nestedInputsProp = undefined as any,
      name = "",
      isHiddenFunc = undefined as IsHiddenOrDisabledFuncType | undefined,
    } = options;

    return handleIsDisabledOrHidden({
      condition: isHiddenOn,
      operation: isHiddenOperation,
      nestedInputsProp,
      name,
      hiddenOrDisabledFields: hiddenFields,
      isHiddenOrDisabledFunc: isHiddenFunc
    });
  };

  const handleIsDisabled = (options) => {
    const {
      isDisabledOn = undefined as IsHiddenConditionType | undefined,
      isDisabledOnOperation = "equal",
      nestedInputsProp = undefined as any,
      name = "",
      isDisabledOnFunc = undefined as IsHiddenOrDisabledFuncType | undefined,
    } = options;

    return handleIsDisabledOrHidden({
      condition: isDisabledOn,
      operation: isDisabledOnOperation,
      nestedInputsProp,
      name,
      hiddenOrDisabledFields: disabledFields,
      isHiddenOrDisabledFunc: isDisabledOnFunc
    });
  };

  const reRenderFormField = (name: string) => {
    setHiddenFields({ ...hiddenFields, [name]: true });
    setTimeout(() => setHiddenFields({ ...hiddenFields, [name]: false }), 0);
  };

  const calculateFields = (newInputs, calculatedOptions = undefined as any, formOptions = {} as any) => {
    const calculatedType = calculatedOptions?.type || "number";

    if (calculatedOptions?.calculatedFunc) {
      const calculatedValue = calculatedOptions.calculatedFunc(newInputs, formOptions);
      if (calculatedValue || calculatedValue === 0 || calculatedValue === "") {
        return {
          ...newInputs,
          [calculatedOptions.valueField]: calculatedValue,
        };
      }
    }
    else {
      let calculatedNum = 0;
      let calculatedString = "";

      calculatedOptions?.calculatedFields?.map(calculatedField => {
        const { name: calculatedName, nestedInputsProp: calculatedNestedInputsProp } = calculatedField;
        const valueToCalculate = calculatedNestedInputsProp !== undefined && newInputs[calculatedNestedInputsProp] ? newInputs[calculatedNestedInputsProp][calculatedName] : newInputs[calculatedName];
        if (valueToCalculate !== undefined) {
          if (calculatedType === "number") {
            const calcFieldValueNum = valueToCalculate ? +valueToCalculate : 0;
            calculatedNum = calculatedNum + calcFieldValueNum;
          }
          else {
            const stringBreak = calculatedString ? " " : "";
            calculatedString = `${calculatedString}${stringBreak}${valueToCalculate}`;
          }
        }
      });

      if ((calculatedNum || calculatedString) && calculatedOptions.valueField) {
        return {
          ...newInputs,
          [calculatedOptions.valueField]: calculatedNum ? calculatedNum : calculatedString ? calculatedString : "",
        };
      }
    }

    return newInputs;
  };

  const getNestedValue = (fieldOptions) => {
    if (!fieldOptions) {
      return undefined;
    }

    const nestedInput: any | undefined = inputs && fieldOptions?.nestedInputsProp !== undefined ? inputs[fieldOptions?.nestedInputsProp] : undefined;
    const nestedDisplay: any | undefined = displayValues && fieldOptions?.nestedInputsProp !== undefined ? displayValues[fieldOptions?.nestedInputsProp] : undefined;
    const nestedInputsValue = nestedInput ? nestedInput[fieldOptions?.name] : undefined;
    const nestedDisplayValue = nestedDisplay ? nestedDisplay[fieldOptions?.name] : undefined;
    return nestedDisplayValue !== undefined ? nestedDisplayValue : nestedInputsValue;
  };

  return {
    inputs,
    setInputs,
    removeInput,
    updateInput,
    formFields,
    setFormFields,
    initialValues,
    isFormInitLoaded,
    setIsFormInitLoaded,
    metaData,
    setMetaData,
    allDropdownOptionsData,
    setAllDropdownOptionsData,
    allDropdownOptionsIsLoading,
    updateDropdownFieldValue,
    refreshDropdownOptions,
    lastUpdated,
    lastUpdatedNestedProp,
    lastValue,
    setLastValue,
    displayValues,
    validations,
    setValidations,
    errors,
    setErrors,
    validateForm,
    hasError,
    handleChange,
    resetForm,
    clearForm,
    isDisabled,
    toggleDisabled,
    setIsDisabled,
    handleIsHidden,
    handleIsDisabled,
    reRenderFormField,
    handleSubmit,
    handleDelete,
    handleClose,
    handleDownloadFile,
    responseData,
    submitting,
    setSubmitting,
    formErrorMessage,
    setFormErrorMessage,
    initSubmittingStates,
    resetSubmittingStates,
    submitError,
    setSubmitError,
    displayMessageLocally,
    setDisplayMessageLocally,
    activeButtonText,
    setActiveButtonText,
    success,
    history,
    fetchData,
    globalCacheDispatch,
    confirmOptions,
    setConfirmOptions,
    handleConfirmFunc,
    setAlertMessage,
    fileDownloads,
    exitEditOnClose,
    addressFields,
    getNestedValue,
    hiddenFields,
    setHiddenFields,
    disabledFields,
    setDisabledFields,
    isFormLoading,
    setIsFormLoading,
    modalIframeSrcPath,
    setModalIframeSrcPath,
    modalIframeSize,
    setModalIframeSize,
    modalIframeHeight,
    setModalIframeHeight,
    iframeModalRef
  };
};

export const logout = (instance, removeCookie) => {
  removeCookie("session_portal");
  removeCookie("session_proxyuserid");
  removeCookie("session_proxyusername");
  removeCookie("session_proxyorganizationcontactid");
  removeCookie("session_permissions");
  removeCookie("session_proxychannels");
  removeCookie("session_proxyisexternal");
  localStorage.clear();
  sessionStorage.clear();
  instance.logoutRedirect();
};

export const checkHoliday = (input) => {
  const dateInput = (input instanceof Date) ? input : new Date(input + "T00:00:00");
  const dateOnly = dateInput.getDate();
  const monthOnly = dateInput.getMonth() + 1;
  const monthDate = `${monthOnly}/${dateOnly}`;
  if ([HolidayConstants.NewYearsDay, HolidayConstants.IndependenceDay, HolidayConstants.ChristmasDay].indexOf(monthDate) > -1) {
    return true;
  }

  const dayOfWeek = dateInput.getDay();
  const weekOfMonth = Math.floor((dateOnly - 1) / 7) + 1;
  const dayWeekOfMonth = `${monthOnly}/${weekOfMonth}/${dayOfWeek}`;
  if ([HolidayConstants.MartinLutherKingsDay,
    HolidayConstants.PresidentsDay,
    HolidayConstants.LaborDay,
    HolidayConstants.Thanksgiving,
    HolidayConstants.DayAfterThanksgiving].indexOf(dayWeekOfMonth) > -1) {
    return true;
  }

  // Memorial Day, checking weekday number from end of the month
  const tempDate = new Date(dateInput);
  const tempDateDate = tempDate.getDate();
  tempDate.setDate(1);
  tempDate.setMonth(tempDate.getMonth() + 1);
  tempDate.setDate(tempDate.getDate() - 1);
  const weekFromEndOfMonth = Math.floor((tempDate.getDate() - dateOnly - 1) / 7) + 1;
  const dayWeekFromEndOfMonth = `${monthOnly}/${weekFromEndOfMonth}/${dayOfWeek}`;
  if (dayWeekFromEndOfMonth === HolidayConstants.MemorialDay[0] || (tempDateDate !== 24 && dayWeekFromEndOfMonth === HolidayConstants.MemorialDay[1])) {
    return true;
  }

  return false;
};

export const checkWeekend = (input) => {
  const dateInput = (input instanceof Date) ? input : new Date(input + "T00:00:00");
  const dayOfWeek = (dateInput instanceof Date) ? dateInput.getDay() : new Date(dateInput).getDay();
  const isWeekend = (dayOfWeek === 6) || (dayOfWeek === 0);
  return isWeekend;
};

export const convertFirstToUppercase = (str: string) => {
  if (str !== "") {
    return str && str[0].toUpperCase() + str.slice(1);
  }
  return "";
};

export const getMockDataPromise = (data, delay = 500) => {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve({
        ok: true,
        json: () => data
      });
    }, delay);
  });
};

export const addTimestamp = (value, time = "T00:00:00") => value ? value + time : "";
export const removeTimestamp = (value) => value?.includes("T") && value.split("T")[0] ? value.split("T")[0] : value;

export const addNotValidProps = (notValidProperties, obj, objField, objName, validationType = GlobalConstants.InputTypes.STRING, custom = false) => {
  const propName = custom ? `custom-${objName}-${objField}-${obj.id}` : `${objName}-${objField}`;
  if (validationType === GlobalConstants.InputTypes.STRING && (Object.keys(obj).includes(objField) && !obj[objField])) {
    notValidProperties.push(propName);
  }
  else if (validationType === GlobalConstants.InputTypes.DOLLAR && (Object.keys(obj).includes(objField) && !obj[objField] || obj[objField] === "0.00")) {
    notValidProperties.push(propName);
  }
  else if (validationType === GlobalConstants.InputTypes.INT) {
    if (Object.keys(obj).includes(objField) && (!obj[objField] && (obj[objField] !== 0 || obj[objField] !== "0"))) {
      notValidProperties.push(propName);
    }
    else if (Object.keys(obj).includes(objField) && !Number.isInteger(parseInt(obj[objField]))) {
      notValidProperties.push(`${objName}-invalid-${objField}`);
    }
  }
};

export const ValidationMessageBundle = ({invalidProps, type, fieldMessageObj}) => {
  return (
    <React.Fragment>
      {Object.entries(fieldMessageObj).map(([field, message]) => 
        <BCValidationMessage invalidProps={invalidProps} prop={`${type}-${field}`} propText={`${message}`}/>
      )}
    </React.Fragment>
  );
};

interface IBCLocalReducerOptions {
  metaDataSuffix?: string;
  storageId?: string;
  hiddenFilters?: any[];
  filterKeysList?: string[];
}

export const useBCGlobalFilter = (dataToFilter = [] as any[], options: IBCLocalReducerOptions = {}) => {
  const { hiddenFilters = [] as any[], storageId = "", metaDataSuffix = "", filterKeysList = [] as any[] } = options;
  const [filteredData, setFilteredData] = useState(dataToFilter);
  const searchStorageId = `searchterm${storageId ? storageId + window.location.pathname : window.location.pathname}`;
  const initialState = {
    filtersListInitial: localStorageGet(`filterslistInitial${storageId || window.location.pathname}`, "session") || [],
    filtersList: localStorageGet(`filterslist${storageId || window.location.pathname}`, "session") || [],
    activeFilters: localStorageGet(`activefilterslist${storageId || window.location.pathname}`, "session") || [],
    isFilterOpen: localStorageGet(`filtersisopen${storageId || window.location.pathname}`, "session") || false,
    searchValue: localStorageGet(searchStorageId, "session") || "",
    searchStorageId: searchStorageId,
    metadata: null
  };

  const [{ activeFilters, isFilterOpen, searchValue, filtersListInitial, filtersList, metadata }, dispatch]: any = useReducer(Reducer, { ...initialState });
  const defaultMetadata = GlobalStoreConstants.metadataApi;
  if (metaDataSuffix || defaultMetadata) {
    useFetchData(options.metaDataSuffix || defaultMetadata, { isFilterMetadata: true, dispatch, hiddenFilters, storageId: options.storageId });
  }

  const getFilteredData = (data, activeFiltersList = activeFilters) => {
    if (data) {
      return globalFilteredData(data, activeFiltersList, metadata);
    }
    else {
      return [];
    }
  };

  const filterBySearchValue = (dataToFilter = [], filterKeys = filterKeysList) => {
    return dataToFilter?.filter(x => Object.entries(x).some(([k, v]: any) => {
      if (v && typeof v === "string") {
        return v.toLowerCase().includes(searchValue?.toLowerCase()) && (filterKeys.length === 0 || filterKeys.includes(k));
      }

      return false;
    }));
  };

  useEffect(() => {
    if (dataToFilter.length > 0) {
      if (searchValue) {
        setFilteredData(filterBySearchValue(getFilteredData(dataToFilter)));
      }
      else {
        setFilteredData(getFilteredData(dataToFilter));
      }
    }
  }, [activeFilters, dataToFilter]);

  useEffect(() => {
    setFilteredData(filterBySearchValue(getFilteredData(dataToFilter)));
  }, [searchValue, dataToFilter]);

  return [{
    activeFilters,
    isFilterOpen,
    searchValue,
    filteredData,
    filtersListInitial,
    filtersList,
    metadata,
    dataToFilter,
    getFilteredData,
    filterBySearchValue,
    setFilteredData
  }, dispatch] as [any, (any) => any];
};

export const arrayHasItems = (data) => data && Array.isArray(data) && data.length > 0;

const isPromise = (promise) => {
  return !!promise && typeof promise.then === "function";
};

interface IUsePromiseRes {
  promiseData: any;
  promiseError: any;
  promiseLoading: boolean;
  setPromiseData: (any) => void;
  setPromiseError: (any) => void;
  setPromiseLoading: (boolean) => void;
  getDropdownOptionsNow: (skipCache?: boolean) => any;
}

interface IUsePromiseAllRes {
  promisesData: any;
  promisesError: any;
  promisesLoading: boolean;
  setPromisesData: (any) => void;
  setPromisesError: (any) => void;
  setPromisesLoading: (boolean) => void;
}

interface IUsePromise {
  initialData?: any
  dependencyHasError?: boolean,
  cleanError?: boolean,
  subPath?: string,
  dropdownOptionsData?: any
  isPromiseAll?: boolean
  dependencyFieldValue?: any
  fieldName?:string
  fieldValue?: any
  addressFieldType?: AddressFieldTypes
  dropdownSkipCache?: boolean;
}

export const getDropdownOption = (name, id = "") => ({ id: id || name, name: name, display: name });
export const getDropdownSemanticOption = (name, id = "") => ({ value: id || name, text: name, key: id || name });
export const getDropdownSemanticOptionsFromData = (data: any[], displayKey = "display") => {
  if (!data || !Array.isArray(data) || data?.length === 0) {
    return [];
  }

  return data.map(item => {
    if (typeof item === "string") {
      return getDropdownSemanticOption(item);
    }
    else {
      const { id, name, display } = item;
      return getDropdownSemanticOption(item[displayKey] || display || name, id);
    }
  });
};

export const getAddressComponentName = (addressComponents: any, component: string) => {
  let componentName = "";
  addressComponents?.map(({ types, long_name }) => {
    if (types.includes(component)) {
      componentName = long_name;
    }
  });

  return componentName;
};

export const usePromise = (promiseToResolve: any, options: IUsePromise) => {
  const {
    initialData = null,
    dependencyHasError = false,
    cleanError = false,
    subPath = "",
    dropdownOptionsData = null,
    isPromiseAll = false,
    dependencyFieldValue,
    fieldValue,
    addressFieldType,
    dropdownSkipCache = false,
  } = options;
  const [promiseData, setPromiseData] = useState(null as any);
  const [promiseError, setPromiseError] = useState(false as any);
  const [promiseLoading, setPromiseLoading] = useState(false);
  const fetchData = !isPromiseAll ? useFetchData()[0]?.fetchData : () => null;
  const urlToFetch = promiseToResolve && typeof promiseToResolve === "string" ? promiseToResolve : "";

  const getAddressOptions = (geoCodeRes) => {
    const options = [] as any[];
    const geoCodeData = geoCodeRes && geoCodeRes[0];

    if (addressFieldType === "city") {
      if (geoCodeData?.postcode_localities?.length > 1) {
        geoCodeData.postcode_localities.map(loc => {
          options.push(getDropdownOption(loc));
        });
        if (fieldValue && !geoCodeData.postcode_localities.includes(fieldValue)) {
          options.push(getDropdownOption(fieldValue));
        }
      }
      else {
        const cityName = getAddressComponentName(geoCodeData?.address_components, "locality");
        if (cityName) {
          options.push(getDropdownOption(cityName));
          if (fieldValue && fieldValue !== cityName) {
            options.push(getDropdownOption(fieldValue));
          }
        }
        else {
          if (fieldValue) {
            options.push(getDropdownOption(fieldValue));
          }
        }
      }
    }
    else if (addressFieldType === "state") {
      const stateName = getAddressComponentName(geoCodeData?.address_components, "administrative_area_level_1");
      if (stateName) {
        options.push(getDropdownOption(stateName));
      }
      else {
        if (fieldValue) {
          options.push(getDropdownOption(fieldValue));
        }
      }
    }
    else if (addressFieldType === "county") {
      const countyName = getAddressComponentName(geoCodeData?.address_components, "administrative_area_level_2");
      if (countyName) {
        const parsedCountyName = countyName && countyName.includes(" County") ? countyName.replace(" County", "") : countyName;
        options.push(getDropdownOption(parsedCountyName));
      }
      else {
        if (fieldValue) {
          options.push(getDropdownOption(fieldValue));
        }
      }
    }

    return options;
  };

  const getDropdownOptions = async (skipCache = false) => {
    if (addressFieldType) {
      if (dependencyFieldValue && dependencyFieldValue.length === 5) {
        setPromiseLoading(true);
        getGeocode({ address: dependencyFieldValue, componentRestrictions: { country: "US" } }).then((results) => {
          setPromiseData(getAddressOptions(results));
          setPromiseLoading(false);
        }).catch((e) => {
          FeatureFlags.IS_DEBUG && console.log(e);
          setPromiseLoading(false);
          setPromiseError(e);
        });
      }
    }
    else {
      promiseToResolve = urlToFetch && !isPromiseAll ? fetchData(promiseToResolve, { skipCache: skipCache || dropdownSkipCache }) : promiseToResolve;
      if (!dependencyHasError && isPromise(promiseToResolve) && !initialData && !isPromiseAll) {
        setPromiseLoading(true);
        try {
          const response = await promiseToResolve.catch(e => {
            setPromiseError(e);
          }).finally(() => setPromiseLoading(false));

          if (response) {
            const data = response?.data;
            const error = response?.error;
            if (data) {
              setPromiseData(subPath && data[subPath] ? data[subPath] : data);
            }
            else if (response.status === 204) {
              setPromiseData([]);
            }
            else if (error) {
              setPromiseError(error);
            }
          }
        } catch (e) {
          setPromiseError(e);
          setPromiseLoading(false);
        }
      }
      else {
        if (dropdownOptionsData && urlToFetch && dropdownOptionsData[urlToFetch]) {
          setPromiseData(subPath && dropdownOptionsData[urlToFetch][subPath] ? dropdownOptionsData[urlToFetch][subPath] : dropdownOptionsData[urlToFetch]);
        }
      }
    }
  };

  useEffect(() => {
    if (initialData) {
      setPromiseData(initialData);
    }
    else {
      runAsyncFunction(getDropdownOptions);
    }
  }, [urlToFetch, dropdownOptionsData, dependencyFieldValue]);

  useEffect(() => {
    let timoutFunc = null as any;
    if (promiseError && cleanError) {
      timoutFunc = setTimeout(() => {
        setPromiseError(null);
      }, 1000);
    }

    return () => {
      if (timoutFunc) {
        clearTimeout(timoutFunc);
      }
    };
  }, [promiseError]);

  return {
    promiseData,
    promiseError,
    promiseLoading,
    setPromiseData,
    setPromiseError,
    setPromiseLoading,
    getDropdownOptionsNow: () => getDropdownOptions(true)
  } as IUsePromiseRes;
};

interface IUsePromiseAll {
  initialData?: any
  resolvedStructure?: any
  fetchData?: any
  isReady?: boolean
  skipSaveToCache?: boolean,
  dropdownOptionsDataArrays?: Record<string, any>
  onDropdownOptionsLoaded?: OnDropdownOptionsLoadedFuncType;
  defaultData?: any
}

export const usePromiseAll = (promisesToResolve: any, options = {} as IUsePromiseAll) => {
  const { initialData = null, fetchData = useFetchData()[0].fetchData, isReady, defaultData = null } = options;
  const [promisesData, setPromisesData] = useState(initialData as any);
  const [promisesError, setPromisesError] = useState(false as any);
  const [promisesLoading, setPromisesLoading] = useState(false);
  const globalCacheDispatch = useBCGlobalCacheReducer()[1];

  useEffect(() => {
    if (promisesToResolve && Array.isArray(promisesToResolve) && !initialData && isReady && !promisesLoading) {
      setPromisesLoading(true);
      try {
        const promises = promisesToResolve.map(p => {
          if (typeof p === "string") {
            const getData = async () => {
              const { data } = await fetchData(p, { skipSaveToCache: false, defaultFetchData: defaultData });
              return { [p] : data };
            };

            return getData();
          }
          else {
            return p;
          }
        });

        Promise.all(promises).then((data) => {
          if (data) {
            const optionsObj = {};
            data.forEach((d) => {
              Object.entries(d).forEach(([key, value]) => {
                optionsObj[key] = value;
              });
            });

            if (Object.keys(optionsObj).length > 0) {
              globalCacheDispatch({
                type: GlobalConstants.BCStoreReducerCases.SET_API_MULTI_CACHE,
                payload: { ...optionsObj },
              });
            }

            setPromisesData(optionsObj);
            setPromisesLoading(false);
          }
        }).catch(e => {
          setPromisesError(e);
          setPromisesLoading(false);
        }).finally(() => setPromisesLoading(false));
      } catch (e) {
        setPromisesError(e);
        setPromisesLoading(false);
      }
    }
  }, [promisesToResolve?.length, isReady]);

  return {
    promisesData,
    promisesError,
    promisesLoading,
    setPromisesData,
    setPromisesError,
    setPromisesLoading
  } as IUsePromiseAllRes;
};

export const isEmptyItem = (obj, item) => {
  return !(obj && obj[item] && obj[item] !== "" && obj[item]) && obj[item] !== false && obj[item] !== 0;
};

export const isZipCode = (value) => {
  return /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(value);
};

export const isValidUrl = (str) => {
  const pattern = new RegExp("^(https?:\\/\\/)?" + // protocol
    "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain name
    "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
    "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
    "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
    "(\\#[-a-z\\d_]*)?$", "i"); // fragment locator
  return pattern.test(str);
};

export const isValidPhoneNumber = (value) => {
  return value.match(/^[a-zA-Z\d\-().\s]{10,15}$/);
};

export const isValidEinTin = (value) => {
  return value?.length === 9;
};

export const isValidAbbreviation = (value) => {
  return value?.length > 0 && value?.length <= 10;
};

export const isValidAmendment = (value) => {
  return value?.length > 0 && value?.length <= 5;
};

export const isValidRoutingNumber = (value) => {
  return value?.length > 0 && value?.length <= 9 && value.match(/^\d+$/);
};

export const isValidSocialSecurityNumber = (value) => {
  return value?.length > 0 && value?.length <= 9;
};

export const isValidPositiveNumber = (value) => {
  return parseFloat(value) > 0;
};

export const convertToCamelCase = (str) => {
  if (typeof str === "string") {
    return str
      .replace(/\s(.)/g, function ($1) { return $1.toUpperCase(); })
      .replace(/\s/g, "")
      .replace(/^(.)/, function ($1) { return $1.toLowerCase(); });
  }

  return "";
};

export const formatDropdownOptions = (options, valueKey = "display", isFormattedArray = false, initialData) => {
  if (options && Array.isArray(options) && !isFormattedArray) {
    const optionDisplayValues = [] as any[];
    options.map(o => {
      const optionText = o[valueKey] || o?.name;
      if (optionText) {
        optionDisplayValues.push({ id: o.id || optionText, text: optionText });
      }
    });

    return [...new Set(optionDisplayValues) as any].map((option, i) => {
      return {
        key: `${option.id}${i}`,
        text: option.text || "",
        value: option.id || ""
      };
    });
  }
  else {
    if (isFormattedArray && initialData) {
      return initialData;
    }
  }

  return options;
};

export const useParseRouteActions = (params = {} as any, allowedActions = [] as any[], allowedSubpages = [] as any[]) => {
  const { search } = useLocation();
  const query = useMemo(() => new URLSearchParams(search), [search]);
  const actionLower = params?.action?.toLowerCase();
  const idLower = params?.id?.toLowerCase();
  const isAdd = actionLower === "add";
  const isEdit = ((actionLower === "edit" && isGuid(idLower)) || isGuid(actionLower));
  const isView = actionLower === "view" && idLower && isGuid(idLower);
  const isCopy = actionLower === "copy" && idLower && isGuid(idLower);
  const isMainPage = !actionLower && !idLower;
  const isSubpageAllowed = allowedSubpages.includes(actionLower);
  let isActionAllowed = allowedActions.includes(actionLower) || isSubpageAllowed || isAdd || isEdit || isCopy || isView || isMainPage;
  const id = isGuid(actionLower) ? actionLower : isGuid(idLower) ? idLower : "";

  if (idLower && !isGuid(idLower)) {
    isActionAllowed = false;
  }

  return { isAdd, isView, isEdit, isCopy, idLower, actionLower, isMainPage, isSubpageAllowed, isActionAllowed, id, query };
};

const parseUrl = (url, id = "") => {
  if (url?.includes("{id}") && id) {
    url = url.replace("{id}", id);
  }
  else if (id) {
    url = `${url}/${id}`;
  }

  if (!url?.startsWith("http")) {
    url = `${process.env.REACT_APP_API_URL}${url && url[0] === "/" ? url.substring(1) : url}`;
  }

  return url;
};

export const navigateToUrl = (url, id = "") => {
  const parsedLink = url?.startsWith("http") ? url : `https://${url}`;
  url = parseUrl(parsedLink, id);
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.click();
};

export const downloadUrl = (url, name = "", id = "") => {
  url = parseUrl(url, id);
  const link = document.createElement("a");
  link.download = name;
  link.href = url;
  link.click();
};

export interface IDownloadOptions {
  fileName?: string,
  id?: any,
  method?: MethodTypes,
  proxyUserId?: string,
  data?: any,
  contentType?: PayloadContentType,
  onSuccessFunc?: any
  buttonText?: string
}

export async function downloadFile(
  fetchUrl,
  {
    fileName,
    id = "",
    method = "GET" as MethodTypes,
    proxyUserId = "",
    data = null as any,
    contentType = "FormData" as PayloadContentType,
    onSuccessFunc = null as any,
    buttonText = ""
  } = {} as IDownloadOptions
) {
  fetchUrl = parseUrl(fetchUrl, id);
  const fetchOptions: any = {
    method,
    headers: APIHeader(proxyUserId, await getAuthToken(), contentType),
  };

  if (data) {
    fetchOptions.body = getParsedData(data, contentType);
  }

  const fetchResponse = await fetch(fetchUrl, fetchOptions);
  if (fetchResponse && fetchResponse.ok) {
    const blob = await fetchResponse.blob() as any;
    const blobURL = window.URL.createObjectURL(blob);
    const tempLink = document.createElement("a");
    tempLink.style.display = "none";
    tempLink.href = blobURL;
    const responseFileName = fetchResponse.headers.get("Content-Disposition")?.split("filename=")[1]?.split(";")?.[0];
    const parsedFileName = responseFileName ? responseFileName?.replace(/^"(.+(?="$))"$/, "$1") : undefined;
    tempLink.setAttribute("download", parsedFileName || fileName || buttonText || "");
    if (typeof tempLink.download === "undefined") {
      tempLink.setAttribute("target", "_blank");
    }
    document.body.appendChild(tempLink);
    tempLink.click();
    document.body.removeChild(tempLink);
    safelyRunFunction(onSuccessFunc);
    setTimeout(() => {
      window.URL.revokeObjectURL(blobURL);
    }, 100);
  }
  else {
    try {
      const errorResponse = await fetchResponse.json();
      if (errorResponse) {
        return { errorResponse } as any;
      }
    } catch (error) {
      return error as any;
    }
  }
}

export async function uploadFile(fetchUrl, data, fetchMethod = "PUT") {
  fetchUrl = parseUrl(fetchUrl);
  const formData = new FormData();
  
  if (data) {
    Object.entries(data).map(([key, value]) => {
      formData.append(key, value as any);
    });
  }

  await fetch(fetchUrl, {
    method: fetchMethod,
    headers: {
      "Access-Control-Allow-Origin": "*",
      "Authorization": `Bearer ${await getAuthToken()}`
    },
    body: formData,
    redirect: "follow"
  }).catch(e => {
    if (FeatureFlags.IS_DEBUG) {
      console.log(e);
    }
  });
}

export const isDateString = (dateString = "") => {
   return /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.?\d{0,2}/.test(dateString);
};

export const getMaskedString = (string, displayLastChar = 4, maskChar = "x", isSocialSecurity = false) => {
  if (string && typeof string === "string" && string.length > displayLastChar) {
    let maskedCars = "";
    const hashLimit = string.length - displayLastChar;
    string.split("").map((char, i) => {
      if (i < hashLimit && hashLimit > 0) {
        maskedCars = `${maskedCars}${maskChar}`;
      }
      else {
        maskedCars = `${maskedCars}${char}`;
      }
      if (isSocialSecurity && (i === 2 || i === 4)) {
        maskedCars = `${maskedCars}-`;
      }
    });
    return maskedCars;
  }
  else {
    return string;
  }
};

export function convertBoolToText(bool) {
  if (bool) {
    return "True";
  }
  else {
    return "False";
  }
}

export const formatCookieString = (string) => {
  switch (string) {
    case "true": return true;
    case "false": return false;
    case "null": return null;
    case "undefined": return undefined;
    default: 
      return string;
  }
};

export const appendIdToPath = ({ id = "", path = "", action = "", params = undefined as Record<string, any>[] | undefined }) => {
  let formattedPath = path || window.location.pathname;
  formattedPath = formattedPath?.startsWith("/") ? formattedPath : "/" + formattedPath;
  formattedPath = formattedPath?.endsWith("/") ? formattedPath : formattedPath + "/";
  formattedPath = action && !path ? formattedPath + action + "/" : formattedPath;

  if (id) {
    if (formattedPath.includes(":id")) {
      formattedPath = formattedPath.replace(":id", id);
    }
    else if (formattedPath.includes("{id}")) {
      formattedPath = formattedPath.replace("{id}", id);
    }
    else {
      formattedPath = `${formattedPath}${id}`;
    }
  }

  if (formattedPath?.endsWith("/")) {
    formattedPath = formattedPath.substring(0, formattedPath.length - 1);
  }

  if (params) {
    formattedPath = appendParams(formattedPath, params);
  }

  return formattedPath;
};

const getSearchParams = (path: string = window.location.pathname) => {
  const [pathPrefix, search] = path.split("?");
  const params = new URLSearchParams(search);
  return { pathPrefix: pathPrefix, params };
};

export const appendParam = (path: string, param: string, paramValue?: string | boolean) => {
  if (!param || !paramValue) {
    return path;
  }

  const { pathPrefix, params } = getSearchParams(path);
  params.append(param, typeof paramValue !== "string" ? paramValue.toString() : paramValue);
  return `${pathPrefix}?${params.toString()}`;
};

export const appendParams = (path: string, paramsArray: Record<string, string | undefined | null | boolean>[]) => {
  const { pathPrefix, params } = getSearchParams(path);

  paramsArray?.map((paramObj) => {
    Object.entries(paramObj).map(([param, paramValue]) => {
      if (param && paramValue) {
        try {
          const paramValueString = typeof paramValue !== "string" ? paramValue.toString() : paramValue;
          params.append(param, paramValueString);
        } catch (error) {
          if (FeatureFlags.IS_DEBUG) {
            console.log(error);
          }
        }
      }
    });
  });

  if (!params.toString()) {
    return path;
  }

  return `${pathPrefix}?${params.toString()}`;
};

export const getPercentageOfAmount = (amount, percent, options = { percentLimit: 0 }) => {
  let percentRes = 0.000;

  if (percent > 0) {
    if (options.percentLimit && percent > options.percentLimit) {
      percentRes = options.percentLimit;
    }
    else {
      percentRes = percent;
    }
  }

  let amountRes = amount * percent / 100 as any;
  amountRes = isNaN(amountRes) || amountRes === undefined ? 0.00 : amountRes.toFixed(2);

  return { percentRes, amountRes };
};

export const getGameChangerIframeSource = () => {
  const isWhiteLabeled = getIsWhiteLabeled();
  if (process.env.REACT_APP_ENV && [Environments.LOCAL, Environments.DEVELOPMENT].indexOf(process.env.REACT_APP_ENV) > -1) {
    return isWhiteLabeled ? GameChangerSources.SYL_DEVELOPMENT : GameChangerSources.DEVELOPMENT;
  }
  else if (process.env.REACT_APP_ENV === Environments.STAGING) {
    return isWhiteLabeled ? GameChangerSources.SYL_STAGING : GameChangerSources.STAGING;
  }
  else if (process.env.REACT_APP_ENV === Environments.PREPROD) {
    return isWhiteLabeled ? GameChangerSources.SYL_PRODUCTION : GameChangerSources.PRODUCTION;
  }
  else if (process.env.REACT_APP_ENV === Environments.PRODUCTION) {
    return isWhiteLabeled ? GameChangerSources.SYL_PRODUCTION : GameChangerSources.PRODUCTION;
  }
};

export const getBulkEditFieldValue = (checkedIdItems: string[], bulkEditState: {[key: string]: unknown}, arr: {[key: string]: unknown}[], field: string): string => {
  if (bulkEditState[field] !== null || !arr || checkedIdItems.length === 0 || !(field in arr[0])) return bulkEditState[field] as string;
  const checkedItems = arr.filter((item) => checkedIdItems.indexOf(item.id as string) !== -1);
  for (let i = 1; i < checkedItems.length; i++) {
    if (checkedItems[i][field] !== checkedItems[0][field]) {
      return GlobalConstants.BulkEditValue.MIXED;
    }
  }
  return checkedItems[0][field] as string || "";
};

export const handleBulkEditFieldChange = (setBulkEditState: React.Dispatch<any>, value: unknown, field: string, type = "string"): void => {
  setBulkEditState((prevBulkEditState) => ({
    ...prevBulkEditState,
    [field]: handleInputType(type, value, undefined, undefined, 50)
  }));
};

export const clearMixedValueBulkEdit = (setBulkEditState: React.Dispatch<any>, value: unknown, field: string): void => {
  if (value === GlobalConstants.BulkEditValue.MIXED) {
    setBulkEditState((prevBulkEditState) => ({...prevBulkEditState, [field]: ""}));
  }
};

export const handleBulkEditModalClose = (setIsBulkEditModalOpen: React.Dispatch<boolean>, setBulkEditState: React.Dispatch<any>, emptyBulkEditInfo: any): void => {
  setBulkEditState({...emptyBulkEditInfo});
  setIsBulkEditModalOpen(false);
};

export const isInViewport = (element) => {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

export const safelyRunFunction = (func: any) => {
  if (func && typeof func === "function") {
    try {
      return func();
    } catch (e) {
      if (FeatureFlags.IS_DEBUG) {
        console.error(e);
      }
    }
  }
};

export const removeProp = (obj, prop = "id" as string | string[]) => {
  if (!obj || typeof obj !== "object") {
    return obj;
  }
  else {
    if (typeof prop === "string") {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [prop]: omit, ...res } = obj;
      return res;
    }
    else if (Array.isArray(prop)) {
      let newObj = { ...obj };
      prop.forEach((p) => {
        newObj = removeProp(newObj, p);
      });

      return newObj;
    }
    else {
      return obj;
    }
  }
};

export const removeNullFromObj = (obj) => {
  for (const property in obj) {
    if (obj[property] === null) {
      delete obj[property];
    }
  }

  return obj;
};

export const getArrayFromInputsWithProps = (inputs: Record<any, any>, originalArr = [] as any[], removeProp = undefined as string | number | undefined) => {
  const inputsUpdated = {};
  const inputsWithoutProps = {};
  const updatedArr = [] as any[];
  Object.entries(inputs).map(([key, value]) => {
    if (removeProp === undefined || (removeProp != key)) {
      if (typeof value === "object") {
        const originalValue = originalArr[key] || {};
        updatedArr.push({ ...originalValue, ...value });
      }
      else {
        inputsUpdated[key] = value;
        inputsWithoutProps[key] = value;
      }
    }
  });

  if (updatedArr.length > 0) {
    updatedArr.map((input, i) => {
      inputsUpdated[i] = input;
    });
  }

  return { updatedArr, inputsWithoutProps, inputsUpdated };
};

export const currencyFormatter = (new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
})).format;

export const base64url_encode = (str: string): string => {
  return btoa(str)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

export const base64url_decode = (value: string): string => {
  const m = value.length % 4;
  return atob(
    value.replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(value.length + (m === 0 ? 0 : 4 - m), "="));
};

const replaceGuidInPath = (path: string) => {
  return path?.split("/").map(pathPartial => {
    if (isGuid(pathPartial)) {
      return "guid";
    }
    else {
      return pathPartial;
    }
  }).join(".") || "";
};

export const getUniqueGridIdString = (tableSuffix, pagePath?: string, encodeString = false) => {
  const { pathPrefix } = getSearchParams(pagePath);
  const { pathPrefix: apiPrefix } = getSearchParams(tableSuffix);
  const parsedPathPrefix = replaceGuidInPath(pathPrefix?.substring(1));
  const parsedTableSuffix = replaceGuidInPath(apiPrefix);
  const uniqueString = `${parsedPathPrefix}__${parsedTableSuffix}`;

  if (encodeString) {
    return base64url_encode(uniqueString);
  }
  else {
    return uniqueString;
  }
};

export const parseGridIdString = (gridId, isEncoded = false) => {
  let decodedGridId = gridId;

  try {
    if (isEncoded) {
      decodedGridId = base64url_decode(gridId);
    }
  } catch (e) {
    if (FeatureFlags.IS_DEBUG) {
      console.log("Error decoding grid id string");
    }
  }

  const [pagePath, tableSuffix] = decodedGridId.split("__");
  return { pagePath, tableSuffix };
};

export const isJson = (str) => {
  if (!str) return false;
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }

  return true;
};

export const isValidDateString = (dateString) => {
  if (dateString && typeof dateString === "string" && (dateString.includes("/") || dateString.includes("-")) && !isNaN(Date.parse(dateString))) {
    const dateToValidate = new Date(dateString);
    return dateToValidate && Object.prototype.toString.call(dateToValidate) === "[object Date]";
  }
};

export const isValidBooleanField = (value) => {
  return typeof value === "boolean" || value === "Yes" || value === "No" || value === "true" || value === "false";
};

export const getDataType = (columnValue) => {
  if (isEmptyValue(columnValue)) {
    return null;
  }
  else if (isValidDateString(columnValue)) {
    return "date";
  }
  else if (isValidBooleanField(columnValue)) {
    return "boolean";
  }
  else if (typeof columnValue === "number") {
    return "number";
  }
  else if (typeof columnValue === "string") {
    return "string";
  }
  else {
    return null;
  }
};

export const getDateFormat = (value) => {
  if (!value.includes("T") || value.includes("T00:00:00")) {
    return "date";
  }

  return "date-time";
};

export const convertPropFromLegacyToCamelCase = (legacyProp) => {
  if (legacyProp && legacyProp.includes("_")) {
    return convertToCamelCase(legacyProp.split("_").join(""));
  }

  return convertToCamelCase(legacyProp);
};

export const isEmptyValue = (value) => {
  return value === undefined || value === null || value === "";
};

export const getSortedColumns = (columns, prevIndex, newIndex) => {
  const sortedColumns = [] as any[];
  const prevColHeader = columns[prevIndex];
  columns.map((ch, i) => {
    if (i !== prevIndex) {
      sortedColumns.push(ch);
      if (newIndex === i) {
        sortedColumns.push(prevColHeader);
      }
    }

    return ch;
  });

  return sortedColumns?.map((col, i) => ({ ...col, index: i })) || [];
};

export const dateTimePayloadFormetter = (date, time) => {
  return `${date?.split("T")[0]}${time ? time : "T00:00:00"}`;
};

export const convertToTimeDropDownValue = (dateTime) => {
  if (dateTime) {
    const time = dateTime.split("T")[1].split(":")[0];
    const minute = parseInt(dateTime.split(":")[1]);
    if (minute <= 20) {
      return `T${time}:00:00`;
    } else if (minute <= 40) {
      return `T${time}:30:00`;
    }
    
    const timeNum = parseInt(time) + 1;
    return `T${timeNum >= 10 ? timeNum : `0${timeNum}`}:00:00`;
  }
};

export const createConfirmationObj = (func: any, message: string, isAlert = false, confirmButtonText = "OK") => ({ message, func, isOpen: true, isAlert, confirmButtonText });

export const convertArrayToOxfordString = (arr, conjunction, isEmpty) => {
  const l = arr.length;
  if (!l) return isEmpty;
  if (l<2) return arr[0];
  if (l<3) return arr.join(` ${conjunction} `);
  arr = arr.slice();
  arr[l-1] = `${conjunction} ${arr[l-1]}`;
  return arr.join(", ");
};

export const parsePercentValue = (value, limit = undefined as number | string | undefined) => {
  if (value) {
    const valueNum = typeof value === "string" ? parseFloat(value.replaceAll(",", "")) : value;
    if (!limit) {
      return valueNum;
    } else {
      if (typeof limit === "string") {
        limit = parseFloat(limit);
      }

      if (valueNum > limit) {
        return limit;
      }
      else {
        return valueNum;
      }
    }
  }
  else {
    return value;
  }
};

export const parseCurrencyValue = (value, limit = undefined as number | string | undefined) => {
  if (value) {
    const valueNum = typeof value === "string" ? parseFloat(value?.replaceAll(",", "")) : value;
    if (!limit) {
      return valueNum;
    } else {
      if (typeof limit === "string") {
        limit = parseFloat(limit);
      }

      if (valueNum > limit) {
        return limit;
      }
      else {
        return valueNum;
      }
    }
  }
  else {
    return value;
  }
};

export const getFormattedMinMaxDate = (date) => {
  if (isEmptyValue(date)) return undefined;
  try {
    if (date && typeof date === "string" && date.length >= 10) {
      return date.substring(0, 10);
    }
    else if (typeof date === "object") {
      return date?.toISOString().substring(0, 10);
    }
    else if (typeof date === "number") {
      const dateMilli = new Date().getTime();
      return new Date(dateMilli + (date * 24 * 60 * 60 * 1000)).toISOString().substring(0, 10);
    }
  } catch (e) {
    if (FeatureFlags.IS_DEBUG) {
      console.log("Error parsing date", e);
    }
  }
};

export const isJsonString = (str) => {
  try {
      JSON.parse(str);
  } 
  catch (e) {
      return false;
  }

  return true;
};

export const handleCreateAuthCookies = (setCookie) => {
  const sylDomain = getEnvironmentDomain();
  for (let i = 0; i < MsalConstants.SessionLength; i++) {
    setCookie(`msal_key${i + 1}` as any, localStorage.key(i), { domain: sylDomain });
    setCookie(`msal_value${i + 1}` as any, localStorage.getItem(localStorage.key(i) as string), { domain: sylDomain });
  }

  localStorage.clear();
};

export const handleApiPromise = async (
  apiCall, 
  mockApiCall, 
  handleErrorResponse = undefined as any,
  isParseJson = true,
  noContentState = [] as any,
  handleError = () => {
    history.pushState(undefined, "", "/error");
    location.reload();
  }
) => {
  try {
    const response = FeatureFlags.IS_MOCK ? await mockApiCall() : await apiCall() as any;
    let data = undefined as any;
    if (response && response.ok) {
      if (response.status === 204) {
        data = noContentState;
      }
      else if (!isParseJson) {
        return true;
      }
      else {
        data = await response.json();
      }
    }
    else if (response && !response.ok) {
      if (handleErrorResponse) {
        await handleErrorResponse(response);
      }
      
      if (!isParseJson) {
        return false;
      }
    }

    return data;
  }
  catch (error) {
    handleError();
  }

  return null;
};

const isSingleLetterWord = (word) => word.match(/([A-Z])/g) && word.length === 1;

export const getParsedPropItemFromLegacy = (legacyStr) => {
  let prop = "";
  let item = "";

  if (typeof legacyStr === "string") {
    prop = convertToCamelCase(legacyStr.split("_").join(""));
    item = convertCamelCase(prop);
  }

  if (item?.match(/([A-Z])(\s)/g)) {
    let parsedItem = "";
    let parsedProp = "";
    const itemWords = item.split(" ");
    itemWords.map((word, index) => {
      if (isSingleLetterWord(word)) {
        parsedItem = `${parsedItem}${word}`;
        parsedProp = `${parsedProp}${word.toLowerCase()}`;
      }
      else {
        parsedItem = `${parsedItem} ${word}`;
        parsedProp = !parsedProp || isSingleLetterWord(itemWords[index - 1]) ? `${parsedProp}${word?.toLowerCase()}` : `${parsedProp}${word}`;
      }
    });

    prop = convertToCamelCase(parsedProp);
    item = parsedItem;
  }

  return { prop, item };
};

export const startsWithCapital = (word) => {
  if (!word) return false;
  return /[A-Z]/.test(word.charAt(0));
};